/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

/**
 * Compares two arrays and checks if they have different elements.
 * Only works with arrays containing strings or numbers.
 * Order of elements does not matter, but frequency does.
 *
 * @param {Array} array1 - The first array to compare (must contain only strings or numbers)
 * @param {Array} array2 - The second array to compare (must contain only strings or numbers)
 * @returns {boolean} - Returns true if arrays have different elements, false if they contain the same elements
 * @throws {TypeError} - Throws if arguments are not arrays or contain elements other than strings or numbers
 *
 * @example
 * // Returns false (arrays have the same elements)
 * areArraysDifferent(["a", "b"], ["b", "a"]);
 *
 * @example
 * // Returns true (arrays have different elements)
 * areArraysDifferent(["a", "b"], ["a", "c"]);
 *
 * @example
 * // Returns true (different frequencies)
 * areArraysDifferent(["a", "a", "b"], ["a", "b", "b"]);
 */

const haveDifferentElements = (array1, array2) => {
  // Type validation - ensure both are arrays
  if (!Array.isArray(array1) || !Array.isArray(array2)) {
    throw new TypeError('Both arguments must be arrays');
  }

  // Validate that all elements are strings or numbers
  const isValidElement = (elem) => typeof elem === 'string' || typeof elem === 'number';

  const hasInvalidElement1 = array1.some(elem => !isValidElement(elem));
  const hasInvalidElement2 = array2.some(elem => !isValidElement(elem));

  if (hasInvalidElement1 || hasInvalidElement2) {
    throw new TypeError('Arrays must contain only strings or numbers');
  }

  // If arrays have different lengths, they must have different elements
  if (array1.length !== array2.length) {
    return true;
  }

  // Create a copy of array2 that we can modify
  const array2Copy = [...array2];

  // Check if each element in array1 exists in array2
  for (const elem of array1) {
    const index = array2Copy.indexOf(elem);

    // If element not found in second array, arrays are different
    if (index === -1) {
      return true;
    }

    // Remove the found element to handle duplicates correctly
    array2Copy.splice(index, 1);
  }

  // If we've matched all elements, arrays have the same elements
  return false;
};

export default haveDifferentElements;
